import styled from '@emotion/styled';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Input, Popconfirm, Tag, Tooltip } from 'antd';
import { CloseCircleFilled, PlusOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';

const Container = styled.div`
  font-size: 14px;
  background-color: var(--fett-bg-elevated);
  .ant-tag {
    height: 32px;
    line-height: 32px;
    margin-top: 16px;
  }
  .tag-input-display {
    height: 32px;
    padding: 0 10px;
    display: flex;
    border: 1px dashed var(--fett-border-strong);
    cursor: pointer;
    align-items: center;
    box-sizing: border-box;
    margin-top: 14px;

    svg {
      margin-right: 6px;
    }
  }

  .add-tag {
    position: relative;
    .error-text {
      color: var(--fett-surface-error-text);
      font-size: 12px;
      position: absolute;
    }
  }
`;

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
`;
const TagItem = styled(Tag)`
  cursor: pointer;
  white-space: normal;
  margin-bottom: 4px;
  background-color: #d7e1fa;
  color: var(--fett-scenario-accent-text);
  max-width: 245px;
  display: flex;
  margin-top: 16px;
  .ant-tag-close-icon {
    display: inline;
    fill: var(--fett-scenario-accent-text);
    color: var(--fett-scenario-accent-text);
    width: 10px;
    margin-left: 7px;
    flex: none;
  }
  .text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  [data-color-mode='dark'] & {
    background-color: rgba(51, 102, 255, 0.16) !important;
    color: #8aadff;
    .ant-tag-close-icon {
      fill: #8aadff;
      color: #8aadff;
    }
  }
`;

const TagInputItem = styled(Input)`
  &.ant-input-affix-wrapper {
    border: none !important;
  }
  width: 315px;
  height: 32px;
  outline: none;
  background-color: var(--fett-bg-elevated);
  margin-top: 16px;
  box-shadow: none;
  outline: 1px solid var(--fett-app-primary-color);
  &:hover {
    border: none !important;
    box-shadow: none;
    outline: 1px solid var(--fett-app-primary-color);
  }
  &:focus {
    border: none !important;
    outline: 1px solid var(--fett-app-primary-color);
    box-shadow: none;
  }
  &.duplicate {
    outline: 1px solid #f05b5b;
    box-shadow: none;
    &:hover,
    &:focus {
      outline: 1px solid #f05b5b;
    }
  }
`;

interface Props {
  tags: string[];
  officialTags: string[];
  onChange: (tags: string[]) => void;
}
const TagWithTooltip = ({ onRemove, text }: { onRemove: (text: string) => void; text: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslate();
  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }
    const scrollWith = ref.current.scrollWidth;
    if (scrollWith > 212) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [text]);
  return (
    <Tooltip visible={visible ? undefined : false} overlay={text}>
      <TagItem
        closable
        closeIcon={
          <Popconfirm
            onConfirm={() => onRemove(text)}
            title={t('ViewBlockList.确认删除该拉黑原因吗？')}
          >
            <CloseCircleFilled />
          </Popconfirm>
        }
      >
        <span className="text" ref={ref}>
          {text}
        </span>
      </TagItem>
    </Tooltip>
  );
};

const TagInput = React.memo((props: Props) => {
  const { tags, onChange, officialTags } = props;
  const [inputValue, setInputValue] = useState('');
  const [focus, setFocus] = useState(false);
  const [duplicateTag, setDuplicateTag] = useState<string | undefined>();
  const { t } = useTranslate();

  const addTag = useCallback(() => {
    setDuplicateTag(undefined);
    if (!inputValue) {
      setFocus(false);
      return;
    }
    if ([...tags, ...officialTags].includes(inputValue)) {
      setDuplicateTag(inputValue);
      return;
    }
    const nv = [...tags, inputValue];
    onChange(nv);
    setInputValue('');
    setFocus(false);
  }, [inputValue, officialTags, onChange, tags]);
  const removeTag = useCallback(
    (item) => {
      onChange(tags.filter((x) => x !== item));
    },
    [onChange, tags],
  );

  return (
    <Container>
      <List>
        {tags.map((item) => {
          return <TagWithTooltip onRemove={removeTag} text={item} key={item} />;
        })}
        {tags.length >= 8 ? null : (
          <div>
            {focus ? (
              <div className="add-tag">
                <TagInputItem
                  onPressEnter={() => addTag()}
                  value={inputValue}
                  placeholder={t('WhatsApp.请输入')}
                  onBlur={addTag}
                  autoFocus
                  maxLength={20}
                  className={duplicateTag ? 'duplicate' : ''}
                  suffix={`${inputValue.length}/20`}
                  onChange={(e) => setInputValue(e.target.value)}
                />
                {duplicateTag === inputValue && (
                  <div className="error-text">{t('ViewBlockList.与已有原因一致，请重新输入')}</div>
                )}
              </div>
            ) : (
              <div
                onClick={() => {
                  setFocus(true);
                  setInputValue('');
                }}
                className="tag-input-display"
              >
                <PlusOutlined />
                {t('ViewBlockList.添加原因')}
              </div>
            )}
          </div>
        )}
      </List>
    </Container>
  );
});

export default TagInput;
